import React, {
  useEffect, useState, useCallback, useRef, memo
} from "react";
import { createPortal } from "react-dom";
import { Navbar } from "react-bootstrap";
import { Link, useLocation } from "react-router-dom";
import "./NavbarMenu.css";

const LINKS = [
  { to: "/home#page-top",     en: "Home",     jp: "ホーム" },
  { to: "/greeting#page-top", en: "Greeting", jp: "ごあいさつ" },
  { to: "/about#page-top",    en: "About",    jp: "会社概要" },
  { to: "/products#page-top", en: "Products", jp: "製品紹介" },
  { to: "/tech#page-top",     en: "Tech",     jp: "技術紹介" },
  { to: "/resources/#page-top", en: "Resources",     jp: "資料・情報" },
  { to: "/topics#page-top",   en: "Topics",   jp: "トピックス" },
  { to: "/recruit#page-top",  en: "Recruit",  jp: "採用" },
  { to: "/contact#page-top",  en: "Contact",  jp: "問い合わせ" },
];

const MORPH_CHARS = "∿〜≈ﾊﾋｳｴｵｶｷｸｹｺｻｼｽｾｿ01∞≡";

function morphText(el, from, to) {
  if (!el) return;
  const maxLen     = Math.max(from.length, to.length);
  const totalSteps = maxLen * 5;
  let step = 0;
  let raf;
  const animate = () => {
    const progress = step / totalSteps;
    let result = "";
    for (let i = 0; i < maxLen; i++) {
      const tgt = to[i] || "";
      if (!tgt) continue;
      const cp = Math.max(0, progress * maxLen - i);
      if (cp >= 1)      result += tgt;
      else if (cp <= 0) result += from[i] || "";
      else result += MORPH_CHARS[Math.floor(Math.random() * MORPH_CHARS.length)];
    }
    el.textContent = result;
    step++;
    if (step <= totalSteps) raf = requestAnimationFrame(animate);
    else el.textContent = to;
  };
  raf = requestAnimationFrame(animate);
  return () => cancelAnimationFrame(raf);
}

/* ── 個別リンク（memoで再レンダー防止） ── */
const FlipLink = memo(function FlipLink({ link, isActive, onClick, scrolledRef }) {
  const frontRef  = useRef(null);
  const cancelRef = useRef(null);
  const shortEn   = link.en.slice(0, 4).toUpperCase();

  useEffect(() => {
    let last = scrolledRef.current;
    let raf;
    const check = () => {
      const now = scrolledRef.current;
      if (now !== last) {
        last = now;
        if (cancelRef.current) cancelRef.current();
        cancelRef.current = morphText(
          frontRef.current,
          now ? link.en : shortEn,
          now ? shortEn : link.en
        );
      }
      raf = requestAnimationFrame(check);
    };
    raf = requestAnimationFrame(check);
    return () => { cancelAnimationFrame(raf); if (cancelRef.current) cancelRef.current(); };
  }, [link.en, shortEn, scrolledRef]);

  return (
    <Link
      to={link.to}
      className={`flip-link${isActive ? " active" : ""}`}
      onClick={onClick}
      aria-label={`${link.en} - ${link.jp}`}
    >
      <span className="front" ref={frontRef}>{link.en}</span>
      <span className="back">{link.jp}</span>
    </Link>
  );
});

/* ── モバイルオーバーレイ（Portal） ── */
function MobileMenuOverlay({ links, onClose, scrolledRef, isActive }) {
  return createPortal(
    <div className="mobile-menu-overlay" role="dialog" aria-modal="true" aria-label="ナビゲーションメニュー">
      <button className="close-btn" onClick={onClose} aria-label="メニューを閉じる">✕</button>
      <nav className="mobile-menu-links">
        {links.map((link) => (
          <FlipLink
            key={link.en}
            link={link}
            isActive={isActive(link.to)}
            onClick={() => onClose(link.to)}
            scrolledRef={scrolledRef}
          />
        ))}
      </nav>
    </div>,
    document.body
  );
}

/* ── メイン ── */
export default function NavbarMenu() {
  const [scrolled, setScrolled] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // ★ isMobile を useState で管理せず、CSSで制御する
  //    → 初期レンダー時に isMobile=true になってリンクが消える問題を根絶
  const [isMobile, setIsMobile] = useState(
    () => window.matchMedia("(max-width: 992px)").matches  // 初期値を即時評価
  );

  const scrolledRef  = useRef(false);
  const logoRef      = useRef(null);
  const prevScrolled = useRef(false);
  const location     = useLocation();

  /* モバイル判定 */
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 992px)");
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  /* スクロール検知 */
  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const now = window.scrollY > 60;
        scrolledRef.current = now;
        setScrolled(now);
        ticking = false;
      });
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  /* ロゴ液体アニメ */
  useEffect(() => {
    const logo = logoRef.current;
    if (!logo || prevScrolled.current === scrolled) return;
    prevScrolled.current = scrolled;
    logo.classList.remove("is-melting", "is-solidifying");
    void logo.offsetWidth;
    logo.classList.add(scrolled ? "is-melting" : "is-solidifying");
    const onEnd = () => logo.classList.remove("is-melting", "is-solidifying");
    logo.addEventListener("animationend", onEnd, { once: true });
    return () => logo.removeEventListener("animationend", onEnd);
  }, [scrolled]);

  useEffect(() => { setExpanded(false); }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = expanded ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [expanded]);

  const handleClose  = useCallback((to) => {
    setExpanded(false);
    // ハッシュがある場合はページトップへスクロール
    if (to && typeof to === "string") {
      const hash = to.split("#")[1];
      if (hash) {
        setTimeout(() => {
          const el = document.getElementById(hash);
          if (el) el.scrollIntoView({ behavior: "smooth" });
          else window.scrollTo({ top: 0, behavior: "smooth" });
        }, 50);
      }
    }
  }, []);
  const handleToggle = useCallback(() => setExpanded(p => !p), []);

  const isActive = useCallback((to) => {
    const path = to.split("#")[0];
    return location.pathname === path || location.pathname.startsWith(path + "/");
  }, [location.pathname]);

  return (
    <>
      <svg style={{ position: "fixed", width: 0, height: 0, overflow: "hidden", top: -1 }}
        aria-hidden="true" focusable="false">
        <defs>
          <filter id="liquid-distort">
            <feTurbulence type="fractalNoise" baseFrequency="0.015 0.08"
              numOctaves="3" seed="5" result="noise" />
            <feDisplacementMap in="SourceGraphic" in2="noise"
              scale="0" xChannelSelector="R" yChannelSelector="G" />
          </filter>
        </defs>
      </svg>

      <Navbar
        expand="lg"
        fixed="top"
        variant="dark"
        className={`navbar-custom${scrolled ? " scrolled" : ""}`}
      >
        <div className="navbar-flex">

          <Navbar.Brand as={Link} to="/" onClick={handleClose}>
            <img
              ref={logoRef}
              src={`${process.env.PUBLIC_URL}/logo.svg`}
              alt="株式会社ZENKYO"
              className={`navbar-logo${scrolled ? " scrolled" : ""}`}
            />
          </Navbar.Brand>

          {/* ハンバーガー：CSSで992px以下のみ表示 */}
          <button
            className="navbar-toggler"
            type="button"
            aria-expanded={expanded}
            aria-label="ナビゲーションメニューを開く"
            onClick={handleToggle}
          >
            <span className="navbar-toggler-icon" />
          </button>

          {/* デスクトップリンク：CSSで992px超のみ表示 */}
          <div className="navbar-links">
            {LINKS.map((link) => (
              <FlipLink
                key={link.en}
                link={link}
                isActive={isActive(link.to)}
                onClick={handleClose}
                scrolledRef={scrolledRef}
              />
            ))}
          </div>

        </div>
      </Navbar>

      {isMobile && expanded && (
        <MobileMenuOverlay
          links={LINKS}
          onClose={handleClose}
          scrolledRef={scrolledRef}
          isActive={isActive}
        />
      )}
    </>
  );
}
